// This supports the previous vehicle response type. VehicleV2 type will be supported in useLeadSender.ts
import VehicleApiResponse from 'types/Apis/VehicleApiResponse';
import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import LeadParams, { ContactInfo, EmailAcceptance } from 'lib/LeadParams';
import { autolistFetch, withOptionalAuth } from 'lib/AutolistAPI';
import { useDispatch } from 'react-redux';
import { logLeadSubmitted } from 'redux-implementations/leadSubmissions';
import { AppDispatch } from 'redux-implementations/makeStore';
import { ResponseRecord } from 'types/Apis/SearchResponse';
import requestSentEvent from 'lib/Tracking/Vdp/Api/RequestSentEvent';
import responseSuccessEvent from 'lib/Tracking/Vdp/Api/ResponseSuccessEvent';
import responseFailureEvent from 'lib/Tracking/Vdp/Api/ResponseFailureEvent';
import { Features } from 'lib/EventSchema';
import { saveMostRecentLeadSubmission } from 'lib/mostRecentLeadSubmission';
import Geocode from 'types/Geocode';
import { isSignedIn } from 'lib/Auth';
import { Dispatch } from 'react';
import { signedIn as sessionSignedIn } from 'redux-implementations/session';
import signInAsGuestUseCase from 'lib/UseCases/signInAsGuest';
import captureException from 'lib/captureException';
import { appendUrlToMessage } from 'lib/appendUrlToMessage';
import useUtmTracking from './useUtmTracking';
import useFirstVisitTracking from './useFirstVisitTracking';
import useEvents from './useEvents';
import useIsPaid from './useIsPaid';
import useHashParamFeatureFlag from './useHashParamFeatureFlag';

interface LeadSubmitterParams {
  vehicle: VehicleApiResponse | ResponseRecord;
  contactInfo: ContactInfo;
  emailAcceptance: EmailAcceptance;
  sourcePage: LeadTrackingPageSource;
  sourceFeature: Features.modalLeadForm | Features.leadForm | Features.quickPicks;
  geolocation: Geocode | undefined;
}

async function ensureSignedIn(email: string, dispatch: Dispatch<any>): Promise<void> {
  if (isSignedIn()) {
    return;
  }

  try {
    const userInfo = await signInAsGuestUseCase(email);
    dispatch(sessionSignedIn(userInfo));
  } catch (e) {
    console.warn(`unable to authenticate user as guest: ${e}`);
    captureException(e);
  }
}

async function postLead(params: LeadParams): Promise<Response> {
  return withOptionalAuth(autolistFetch)(`/leads`, {
    body: JSON.stringify(params),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function useLeadSubmitter(
  sourcePageCategory?: string
): (params: LeadSubmitterParams) => Promise<any> {
  const isPaid = useIsPaid();
  const firstVisitTracking = useFirstVisitTracking();
  const utmTracking = useUtmTracking();
  const logger = useEvents();
  const dispatch = useDispatch<AppDispatch>();
  const isOnlineToOffline = useHashParamFeatureFlag('isOnlineToOffline');

  return async function leadSubmitter({
    vehicle,
    contactInfo,
    emailAcceptance,
    sourcePage,
    geolocation,
    sourceFeature: feature,
  }: LeadSubmitterParams): Promise<any> {
    saveMostRecentLeadSubmission({
      name: contactInfo.name,
      phone: contactInfo.phone,
      email: contactInfo.email,
    });

    const params = new LeadParams({
      vin: vehicle.vin,
      isRegional: vehicle.regional,
      partnerType: vehicle.partner_type,
      trackingParams: vehicle.tracking_params,
      emailAcceptance,
      contactInfo: isOnlineToOffline
        ? appendUrlToMessage(contactInfo, vehicle.tracking_params.remote_dealer_id, vehicle.vin)
        : contactInfo,
      geolocation,
      firstVisitTracking,
      utmTracking,
      isPaid,
      sourcePage,
    });

    const sourcePageUrl = `${window.location.pathname}/${vehicle.vin}`;

    await ensureSignedIn(contactInfo.email, dispatch);

    logger.log(
      requestSentEvent({ feature, trackingParams: params.trackingParams, vin: vehicle.vin })
    );

    const response = await postLead(params);

    if (response.ok) {
      logger.log(
        responseSuccessEvent({
          feature,
          statusCode: response.status,
          trackingParams: params.trackingParams,
          sourcePageUrl,
          sourcePageCategory: sourcePageCategory || '',
          vin: vehicle.vin,
        })
      );
    } else {
      const { status, statusText, url } = response;
      logger.log(
        responseFailureEvent({
          feature,
          errorCode: status,
          errorMessage: statusText,
          trackingParams: params.trackingParams,
          sourcePageUrl,
          sourcePageCategory: sourcePageCategory || '',
          vin: vehicle.vin,
        })
      );
      throw new Error(`Got status ${status} while fetching ${url}`);
    }

    dispatch<any>(logLeadSubmitted(params.toJSON()));
    return response.json();
  };
}
